
import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ChevronDown, ChevronUp } from "lucide-react";

interface DamageProduct {
  product: string;
  count: number;
  percentage: number;
  note?: string;
}

interface TopDamageProductsProps {
  products: DamageProduct[];
}

type SortKey = 'product' | 'count' | 'percentage';
type SortDirection = 'asc' | 'desc';

const TopDamageProducts: React.FC<TopDamageProductsProps> = ({ products }) => {
  const [sortKey, setSortKey] = useState<SortKey>('count');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedProducts = [...products].sort((a, b) => {
    const multiplier = sortDir === 'asc' ? 1 : -1;
    
    if (sortKey === 'product') {
      return a.product.localeCompare(b.product) * multiplier;
    } else if (sortKey === 'count') {
      return (a.count - b.count) * multiplier;
    } else {
      return (a.percentage - b.percentage) * multiplier;
    }
  });

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">5. 🔍 Top Foutgevoelige Producten</h2>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Producten met schades</CardTitle>
          <CardDescription>Top 5 producten met meeste schadegevallen</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader className="bg-muted/30 sticky top-0">
              <TableRow>
                <TableHead 
                  className="w-[30%] cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort('product')}
                >
                  <div className="flex items-center gap-1">
                    Product
                    {sortKey === 'product' && (
                      sortDir === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50 transition-colors w-[15%]"
                  onClick={() => handleSort('count')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Aantal
                    {sortKey === 'count' && (
                      sortDir === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="text-right cursor-pointer hover:bg-muted/50 transition-colors w-[15%]"
                  onClick={() => handleSort('percentage')}
                >
                  <div className="flex items-center justify-end gap-1">
                    %
                    {sortKey === 'percentage' && (
                      sortDir === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="w-[40%]">Opmerking</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedProducts.map((item, index) => (
                <TableRow key={index} className="hover:bg-muted/20 transition-colors">
                  <TableCell className="font-medium">{item.product}</TableCell>
                  <TableCell className="text-right">{item.count}</TableCell>
                  <TableCell className="text-right">{item.percentage.toFixed(2)}%</TableCell>
                  <TableCell>{item.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
};

export default TopDamageProducts;
